import { LitElement, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { TranslationsSections } from '../index';
import { getGeneralSectionIncidentData } from '../../../utils/xdr.utils';
import { IncidentType, XdrIncidentTabPanels } from '../../incidentsSideMenu/types';
import { FontSizes } from '../../../types/common/types';
import { IncidentEvents } from '../../incidentsGrid/types';

/**
 * @summary Type here the description of this component
 * @tag xdr-incident-details
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('xdr-incident-details')
export class XdrIncidentDetailsWebComponent extends LitElement {
  @property({
    type: Object,
    attribute: false,
  })
  public translations: TranslationsSections;

  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  @property({
    type: Object,
    attribute: false,
  })
  incident;

  render() {
    return html`
      <div class="header mt-6">
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2">${this.translations.incidentDetailsTitle}</gz-title>
      </div>
      <div class="content flex-1">
        <incident-general-section
          .title=${this.translations.generalSectionLabels.sectionTitle}
          .incident=${getGeneralSectionIncidentData(this.incident)}
          .translations=${this.translations.generalSectionLabels}>
        </incident-general-section>
        <incident-details-analysis-section
          .status=${Translator.translateEdrIncidentStatus(this.incident.status)}
          .assignee=${this.incident.extra?.assigneeName}
          .priority=${Translator.translateEdrIncidentPriority(this.incident.priority)}
          .translations=${this.translations.analysisSectionLabels}>
        </incident-details-analysis-section>
        <incident-attack-info
          .translations=${this.translations.attackInfoSectionLabels}
          .actionTaken=${this.getActionTaken(this.incident)}
          .resources=${this.getResources(this.incident)}
          .entities=${this.getEntities(this.incident)}
          .alerts="${this.getAlerts(this.incident)}"
          .correlatedIncidents=${this.getCorrelatedIncidents(this.incident)}
          .killChainPhases=${this.getKillChainPhases(this.incident)}
          .incidentData=${this.incident}>
        </incident-attack-info>
        <incident-details-techniques-section
          .mitreTags=${this.incident.mitreTags}
          .translations="${this.translations.techniquesSectionLabels}">
        </incident-details-techniques-section>
      </div>
      <div class="footer d-flex align-items-center">
        <gz-button
          @click=${e => this.handleViewIncidentClicked(e, this.incident)}
          buttonheight="medium"
          buttonfontsize="medium"
        >${this.translations.viewIncidentButton}
        </gz-button>
        <gz-button
          @click="${e => this.handleViewReponsesButtonClicked(e, this.incident)}"
          class="ml-2"
          type="quiet"
          color="secondary"
          buttonheight="medium"
          buttonfontsize="medium"
        >${this.translations.viewResponsesButton}
        </gz-button
      </div>
    `;
  }

  constructor() {
    super();
  }

  handleViewReponsesButtonClicked(e, incident) {
    const event = new CustomEvent(IncidentEvents.SHOW_INCIDENT, {
      detail: {
        incident: incident,
        extraDetails: {
          tab: XdrIncidentTabPanels.RESPONSE,
        },
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  getResources(incident) {
    const { resources } = incident;
    return resources;
  }

  getAlerts(incident) {
    const { alerts } = incident;
    return alerts;
  }

  getKillChainPhases(incident) {
    const { specifics } = incident;
    return specifics?.killChainPhases;
  }

  getActionTaken(incident) {
    return incident.extra.actionTakenValue;
  }

  getEntities(incident) {
    const { entities } = incident;
    return entities;
  }

  /**
   * when we have edr incident that is correlated in an xdr incident
   * we need to also display the endpoint/server that is part of both incidents
   *
   * @param currentIncident
   * @returns {any}
   */
  getCorrelatedIncidents(currentIncident) {
    const { correlatedIncidents } = currentIncident;
    correlatedIncidents?.forEach(correlatedIncident => {
      currentIncident.entities.forEach(entity => {
        entity.nodes.forEach(node => {
          node.incidents?.forEach(incident => {
            if (correlatedIncident.id === incident._id && incident.type === IncidentType.EDR) {
              correlatedIncident.node = {
                name: node.name,
                uid: node.uid,
              };
            }
          });
        });
      });
    });
    return correlatedIncidents;
  }

  handleViewIncidentClicked(e, incident) {
    const event = new CustomEvent(IncidentEvents.SHOW_INCIDENT, {
      detail: {
        incident: incident,
        extraDetails: {
          tab: XdrIncidentTabPanels.OVERVIEW,
        },
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'xdr-incident-details': XdrIncidentDetailsWebComponent;
  }
}
